import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Reemplazo de window.confirm() por un modal propio, no bloqueante y con el
 * estilo de la app. Uso:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, confirmText, tone: 'danger' });
 *   if (!ok) return;
 *
 * También admite confirm('mensaje') como atajo.
 */

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: '¿Confirmar acción?',
  message: '',
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  tone: 'default', // 'default' | 'danger'
};

const DangerIcon = (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 9v4m0 4h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const QuestionIcon = (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9.4 9.4a2.6 2.6 0 0 1 5.06.86c0 1.73-2.6 2.6-2.6 2.6M12 17h.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function ConfirmModal({ opts, onCancel, onConfirm }) {
  const confirmBtnRef = useRef(null);
  const danger = opts.tone === 'danger';

  useEffect(() => {
    confirmBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, onConfirm]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={opts.title}
    >
      <div
        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-mesh-hero shadow-card animate-fade-up">
        <div className="pointer-events-none absolute inset-0 bg-noise" />
        <div className="relative px-7 pb-7 pt-8 text-center">
          <div
            className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${
              danger
                ? 'bg-rose-500/15 text-rose-300 shadow-[0_0_40px_-8px_rgba(244,63,94,0.5)]'
                : 'bg-gold/15 text-gold-bright shadow-glow'
            }`}
          >
            {danger ? DangerIcon : QuestionIcon}
          </div>

          <h2 className="font-display text-xl font-bold tracking-tight text-cream">
            {opts.title}
          </h2>
          {opts.message ? (
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-cream/70">
              {opts.message}
            </p>
          ) : null}

          <div className="mt-7 flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-xl border border-white/15 px-4 py-3 text-sm font-semibold text-cream/80 transition hover:bg-white/5"
            >
              {opts.cancelText}
            </button>
            <button
              ref={confirmBtnRef}
              type="button"
              onClick={onConfirm}
              className={`flex-1 rounded-xl px-4 py-3 text-sm font-bold shadow-lift transition hover:brightness-105 ${
                danger ? 'bg-rose-400 text-white' : 'bg-btn-gold text-ink'
              }`}
            >
              {opts.confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((options) => {
    const raw = typeof options === 'string' ? { message: options } : options || {};
    return new Promise((resolve) => {
      // Si ya había un diálogo abierto, lo resolvemos como cancelado.
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setState({ opts: { ...DEFAULTS, ...raw } });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state ? (
        <ConfirmModal
          opts={state.opts}
          onCancel={() => settle(false)}
          onConfirm={() => settle(true)}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>.');
  }
  return ctx;
}
